// Enhanced instant wellness service with OpenAI integration
// Provides professional-grade therapeutic support using advanced AI

export const instantWellnessService = {
  async getWellnessGuidance(query, userId, session = null) {
    try {
      // First try the backend API for enhanced responses
      if (session?.access_token) {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/instant-wellness`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            query: query,
            userId: userId
          })
        });

        if (response.ok) {
          const data = await response.json();
          return data;
        } else {
          console.warn('Backend API returned error:', response.status, response.statusText);
          // Fall back to enhanced local service
          return await this.getEnhancedLocalGuidance(query, userId);
        }
      } else {
        console.warn('No session token available, using enhanced local service');
        // Fall back to enhanced local service
        return await this.getEnhancedLocalGuidance(query, userId);
      }
    } catch (error) {
      console.error('Error calling backend API:', error);
      // Fall back to enhanced local service
      return await this.getEnhancedLocalGuidance(query, userId);
    }
  },

  async getEnhancedLocalGuidance(query, userId) {
    try {
      // Simulate network delay for realistic feel
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const concerns = this.identifyMainConcerns(query);
      const emotionalState = this.detectEmotionalState(query);
      const relationshipContext = this.detectRelationshipContext(query);
      
      const immediateRelief = this.generatePersonalizedImmediateRelief(query, concerns, emotionalState, relationshipContext);
      const breathingExercises = this.generateBreathingExercises(concerns);
      const cognitiveStrategies = this.generateAdvancedCognitiveStrategies(query, concerns, emotionalState, relationshipContext);
      const physicalActions = this.generatePhysicalActions(concerns);
      const longTermStrategies = this.generateEvidenceBasedStrategies(concerns, relationshipContext);
      const affirmation = this.generatePersonalizedAffirmation(query, emotionalState, concerns);
      
      return {
        immediateRelief,
        breathingExercises,
        cognitiveStrategies,
        physicalActions,
        longTermStrategies,
        affirmation,
        concerns,
        emotionalState
      };
    } catch (error) {
      console.error('Error in enhanced local wellness service:', error);
      throw new Error('Failed to generate wellness guidance');
    }
  },

  detectRelationshipContext(query) {
    const lowerQuery = query.toLowerCase();
    const context = {};
    
    // Gottman Method indicators
    if (lowerQuery.includes('partner') || lowerQuery.includes('spouse') || lowerQuery.includes('marriage') || lowerQuery.includes('relationship')) {
      context.type = 'romantic';
      
      // Four Horsemen detection
      if (lowerQuery.includes('always') || lowerQuery.includes('never') || lowerQuery.includes('you always') || lowerQuery.includes('you never')) {
        context.gottmanIssue = 'criticism';
      }
      if (lowerQuery.includes('stupid') || lowerQuery.includes('pathetic') || lowerQuery.includes('worthless') || lowerQuery.includes('disgusting')) {
        context.gottmanIssue = 'contempt';
      }
      if (lowerQuery.includes('not my fault') || lowerQuery.includes('but you') || lowerQuery.includes('i didn\'t')) {
        context.gottmanIssue = 'defensiveness';
      }
      if (lowerQuery.includes('ignore') || lowerQuery.includes('silent treatment') || lowerQuery.includes('shut down') || lowerQuery.includes('won\'t talk')) {
        context.gottmanIssue = 'stonewalling';
      }
    }
    
    if (lowerQuery.includes('family') || lowerQuery.includes('parent') || lowerQuery.includes('sibling') || lowerQuery.includes('child')) {
      context.type = 'family';
    }
    
    if (lowerQuery.includes('friend') || lowerQuery.includes('social') || lowerQuery.includes('coworker') || lowerQuery.includes('colleague')) {
      context.type = 'social';
    }
    
    return context;
  },

  generatePersonalizedImmediateRelief(query, concerns, emotionalState, relationshipContext) {
    const personalizedStrategies = [];
    const lowerQuery = query.toLowerCase();
    
    // Analyze specific keywords and phrases from the user's query for personalization
    const queryWords = lowerQuery.split(' ');
    const emotionalWords = queryWords.filter(word => 
      ['angry', 'sad', 'frustrated', 'anxious', 'worried', 'scared', 'hurt', 'upset', 'overwhelmed', 
       'stressed', 'lonely', 'confused', 'disappointed', 'guilty', 'ashamed', 'hopeless'].includes(word)
    );
    
    // Extract specific situations mentioned in the query
    const situationIndicators = {
      work: ['work', 'job', 'boss', 'colleague', 'office', 'meeting', 'deadline', 'project'],
      family: ['family', 'mom', 'dad', 'parent', 'sibling', 'child', 'kids'],
      health: ['sick', 'pain', 'doctor', 'hospital', 'diagnosis', 'medication'],
      financial: ['money', 'bills', 'debt', 'financial', 'broke', 'expensive', 'afford'],
      social: ['friends', 'social', 'alone', 'rejected', 'excluded', 'party']
    };
    
    let detectedSituation = 'general';
    for (const [situation, keywords] of Object.entries(situationIndicators)) {
      if (keywords.some(keyword => lowerQuery.includes(keyword))) {
        detectedSituation = situation;
        break;
      }
    }
    
    // Generate situation-specific relief strategies that reference their actual words
    if (detectedSituation === 'work') {
      if (lowerQuery.includes('boss') || lowerQuery.includes('manager')) {
        personalizedStrategies.push("For your situation with your boss, try taking three deep breaths before your next interaction to help you respond rather than react");
        personalizedStrategies.push("Consider that your boss might be under pressure too - this doesn't excuse poor behavior, but it can help you stay centered");
      } else if (lowerQuery.includes('deadline') || lowerQuery.includes('project')) {
        personalizedStrategies.push("Break down what you're facing with this deadline into just the next single step you can take right now");
        personalizedStrategies.push("Remember: you've handled challenging projects before, and you have the skills to handle this one too");
      } else {
        personalizedStrategies.push("For your work situation, try the '5-4-3-2-1' grounding technique: notice 5 things you can see, 4 you can touch, 3 you can hear, 2 you can smell, 1 you can taste");
        personalizedStrategies.push("Set a boundary with work stress by choosing one small self-care action you'll do when you get home today");
      }
    }
    
    // Handle relationship-specific interventions using Gottman Method
    if (relationshipContext.type === 'romantic') {
      const gottmanStrategies = [];
      
      if (relationshipContext.gottmanIssue === 'criticism') {
        gottmanStrategies.push(`Instead of focusing on what your partner ${lowerQuery.includes('always') ? 'always' : 'never'} does, try expressing what you need: 'I feel...' instead of 'You ${lowerQuery.includes('always') ? 'always' : 'never'}...'`);
        gottmanStrategies.push("Right now, think of one specific thing your partner did recently that you appreciated - even something small");
      }
      
      if (relationshipContext.gottmanIssue === 'contempt') {
        gottmanStrategies.push("I hear the pain in what you're sharing. Underneath anger is often hurt - what are you really feeling hurt about in this situation?");
        gottmanStrategies.push("Try to remember: your partner is human too. What might they be struggling with that you don't know about?");
      }
      
      if (relationshipContext.gottmanIssue === 'defensiveness') {
        gottmanStrategies.push("For this specific conflict, try taking responsibility for even 1% of what happened - this can break the defensive cycle");
        gottmanStrategies.push("Ask yourself: what is your partner really trying to tell you underneath their words?");
      }
      
      if (relationshipContext.gottmanIssue === 'stonewalling') {
        gottmanStrategies.push("If you're feeling overwhelmed in this conversation, try saying: 'I need a 20-minute break to calm down, then let's continue this conversation'");
        gottmanStrategies.push("Use this time to actually self-soothe - your heart rate needs to drop below 100 BPM to have a productive conversation");
      }
      
      personalizedStrategies.push(...gottmanStrategies);
    }
    
    // Add emotional state-specific strategies that acknowledge their feelings
    if (emotionalWords.length > 0) {
      const primaryEmotion = emotionalWords[0];
      personalizedStrategies.push(`I can hear that you're feeling ${primaryEmotion}. That's a completely valid response to what you're going through`);
      
      if (primaryEmotion === 'overwhelmed') {
        personalizedStrategies.push("When everything feels too much, try this: pick just ONE thing from your list and focus only on that for the next 10 minutes");
      } else if (primaryEmotion === 'anxious' || primaryEmotion === 'worried') {
        personalizedStrategies.push("For your anxiety right now, try the 'What if it goes right?' question instead of focusing on what could go wrong");
      } else if (primaryEmotion === 'lonely') {
        personalizedStrategies.push("Loneliness is painful, and you're not alone in feeling this way. Can you reach out to just one person today, even with a simple text?");
      }
    }
    
    // If no specific strategies were generated, fall back to base strategies
    if (personalizedStrategies.length === 0) {
      const baseStrategies = this.generateImmediateRelief(concerns, emotionalState);
      return baseStrategies;
    }
    
    // Limit to 3-4 most relevant strategies
    return personalizedStrategies.slice(0, 4);
  },

  generateAdvancedCognitiveStrategies(query, concerns, emotionalState, relationshipContext) {
    const strategies = [];
    const lowerQuery = query.toLowerCase();
    
    // Query-specific cognitive strategies that reference their actual situation
    if (lowerQuery.includes('what if') || lowerQuery.includes('catastrophic') || lowerQuery.includes('worst case')) {
      strategies.push("I notice you're thinking about worst-case scenarios. Try this: for every 'what if something bad happens,' ask yourself 'what if it goes better than expected?'");
      strategies.push("Challenge this specific worry by asking: 'What evidence do I actually have that this worst-case scenario will happen?'");
    }
    
    if (lowerQuery.includes('always') || lowerQuery.includes('never') || lowerQuery.includes('everyone') || lowerQuery.includes('everything')) {
      strategies.push("I hear some 'all-or-nothing' thinking in what you're sharing. Life usually exists in shades of gray - what's the middle ground here?");
      strategies.push("When you think 'always' or 'never,' try adding 'sometimes' - this can help you see the fuller picture");
    }
    
    if (lowerQuery.includes('should') || lowerQuery.includes('must') || lowerQuery.includes('have to')) {
      strategies.push("I notice some 'should' statements in your thinking. Try replacing 'I should' with 'I could' or 'I choose to' - how does that feel different?");
      strategies.push("Ask yourself: whose voice is behind these 'shoulds'? Are these your authentic values, or expectations from others?");
    }
    
    if (lowerQuery.includes('stupid') || lowerQuery.includes('failure') || lowerQuery.includes('worthless') || lowerQuery.includes('pathetic')) {
      strategies.push("I hear some harsh self-criticism. Would you speak to a good friend the way you're speaking to yourself right now?");
      strategies.push("Try this experiment: what would someone who cares about you say about this situation? Can you borrow their voice for a moment?");
    }
    
    // CBT-based cognitive reframing for specific concerns
    if (concerns.includes('anxiety')) {
      if (lowerQuery.includes('control')) {
        strategies.push("For your anxiety about control, try this: make two lists - 'What I can control' and 'What I cannot control' - focus your energy on the first list");
      } else {
        strategies.push("Practice the 'Best Friend' technique for this specific worry: What would you tell your best friend if they shared this same concern with you?");
        strategies.push("Use the 'Zoom Out' method: How will this specific situation matter in 5 years? In 1 year? In 1 month?");
      }
    }
    
    if (concerns.includes('depression')) {
      strategies.push("Notice the 'all-or-nothing' thinking pattern in this situation. Life exists in shades of gray - what's one small positive thing about today?");
      strategies.push("Practice 'opposite action' for this specific feeling - when depression says 'stay in bed,' do one tiny opposite action like standing up or opening the curtains");
    }
    
    if (relationshipContext.type === 'romantic') {
      strategies.push("For this specific situation with your partner, practice the 'Benefit of the Doubt' exercise: What if their behavior came from stress rather than wanting to hurt you?");
      strategies.push("Use the 'Love Map' technique: What might your partner be struggling with right now that you don't know about?");
    }
    
    // DBT-based distress tolerance for high emotional intensity
    if (emotionalState === 'high-intensity') {
      strategies.push("Practice 'Radical Acceptance' for this specific situation - can you accept this moment exactly as it is, without trying to change it right now?");
      strategies.push("Use 'TIPP' technique: Temperature (cold water on face), Intense exercise (10 jumping jacks), Paced breathing, Paired muscle relaxation");
    }
    
    // Work-specific cognitive strategies
    if (lowerQuery.includes('work') || lowerQuery.includes('job') || lowerQuery.includes('boss')) {
      strategies.push("For work stress, try 'mental compartmentalization' - set a specific time limit to worry about this work issue, then mentally 'close the file'");
      if (lowerQuery.includes('boss') || lowerQuery.includes('manager')) {
        strategies.push("Consider your boss's perspective: they might be under pressure too. This doesn't excuse poor behavior, but it can help you respond strategically rather than reactively");
      }
    }
    
    return strategies.slice(0, 4);
  },

  generateSomaticInterventions(concerns, emotionalState) {
    const interventions = [];
    
    // Trauma-informed somatic practices
    if (concerns.includes('anxiety') || concerns.includes('stress')) {
      interventions.push("Practice 'Grounding': Press your feet firmly into the floor and feel your body's connection to the earth");
      interventions.push("Try 'Bilateral Stimulation': Cross your arms over your chest and alternately pat your shoulders while breathing deeply");
      interventions.push("Use 'Progressive Muscle Release': Tense your shoulders for 5 seconds, then release and notice the contrast");
      interventions.push("Practice 'Vagus Nerve Stimulation': Hum or make 'ahh' sounds to activate your body's natural calming response");
    }
    
    if (concerns.includes('depression')) {
      interventions.push("Try 'Energy Activation': Stand up and shake out your arms and legs for 30 seconds to move stagnant energy");
      interventions.push("Practice 'Heart Opening': Stand tall, open your arms wide, and take three deep breaths to counteract the collapsed posture of depression");
      interventions.push("Use 'Sunlight Therapy': Step outside for 2 minutes and let sunlight touch your face - this helps regulate mood hormones");
    }
    
    if (emotionalState === 'high-intensity') {
      interventions.push("Try 'Cold Water Reset': Splash cold water on your wrists and behind your ears to activate your parasympathetic nervous system");
      interventions.push("Practice 'Butterfly Hugs': Wrap your arms around yourself and alternately pat your shoulders to self-soothe");
    }
    
    return interventions.slice(0, 4);
  },

  generateEvidenceBasedStrategies(concerns, relationshipContext) {
    const strategies = [];
    
    // Gottman Method for relationships
    if (relationshipContext.type === 'romantic') {
      strategies.push("Establish a weekly 'State of the Union' meeting - 20 minutes to appreciate each other and address concerns without distractions");
      strategies.push("Practice the 'Six-Second Kiss' daily - it takes 6 seconds to create a moment of connection and intimacy");
      strategies.push("Build your 'Love Map' by asking open-ended questions about your partner's inner world, dreams, and concerns");
      strategies.push("Create rituals of connection - small daily moments like coffee together or a bedtime check-in");
    }
    
    // Mindfulness-based practices
    if (concerns.includes('anxiety') || concerns.includes('stress')) {
      strategies.push("Develop a 'Mindful Transition' ritual between activities - 3 conscious breaths before moving to the next task");
      strategies.push("Practice the 'STOP' technique regularly: Stop, Take a breath, Observe your thoughts/feelings, Proceed with intention");
      strategies.push("Create a 'Worry Window' - designate 15 minutes daily to write down worries, then close the window and focus on the present");
    }
    
    // Positive psychology interventions
    if (concerns.includes('depression') || concerns.includes('self-esteem')) {
      strategies.push("Keep a 'Three Good Things' journal - write down three positive things that happened each day and why they were meaningful");
      strategies.push("Practice 'Strengths Identification' - identify your top 5 character strengths and find ways to use them daily");
      strategies.push("Develop 'Gratitude Visits' - write a letter to someone who helped you and read it to them in person or over the phone");
    }
    
    return strategies.slice(0, 4);
  },

  generatePersonalizedAffirmation(query, emotionalState, concerns) {
    const lowerQuery = query.toLowerCase();
    
    // Analyze the specific situation mentioned in the query
    const situationSpecificAffirmations = {};
    
    // Work-related affirmations
    if (lowerQuery.includes('work') || lowerQuery.includes('job') || lowerQuery.includes('boss') || lowerQuery.includes('deadline')) {
      situationSpecificAffirmations.work = [
        "I bring valuable skills and perspective to my work, and I can handle the challenges I'm facing",
        "I am capable of setting healthy boundaries at work while still being professional and effective",
        "Every work challenge is an opportunity for me to grow stronger and more confident in my abilities"
      ];
    }
    
    // Relationship-specific affirmations
    if (lowerQuery.includes('partner') || lowerQuery.includes('relationship') || lowerQuery.includes('spouse') || lowerQuery.includes('marriage')) {
      situationSpecificAffirmations.relationship = [
        "I am worthy of love and respect in my relationships, and I can communicate my needs with kindness",
        "Every relationship challenge is an opportunity for deeper understanding and stronger connection",
        "I choose to approach my relationship with curiosity and compassion, both for myself and my partner"
      ];
    }
    
    // Family-related affirmations
    if (lowerQuery.includes('family') || lowerQuery.includes('parent') || lowerQuery.includes('mom') || lowerQuery.includes('dad')) {
      situationSpecificAffirmations.family = [
        "I can love my family while also honoring my own needs and boundaries",
        "I am not responsible for fixing everyone in my family, and I can offer support without sacrificing myself",
        "I choose to break cycles of hurt and create healthier patterns in my family relationships"
      ];
    }
    
    // Health-related affirmations
    if (lowerQuery.includes('sick') || lowerQuery.includes('health') || lowerQuery.includes('pain') || lowerQuery.includes('doctor')) {
      situationSpecificAffirmations.health = [
        "My body is doing its best to heal, and I trust in my body's wisdom and resilience",
        "I can advocate for my health needs while also accepting what is beyond my control",
        "Each day of healing, no matter how small the progress, is a victory worth celebrating"
      ];
    }
    
    // Financial stress affirmations
    if (lowerQuery.includes('money') || lowerQuery.includes('financial') || lowerQuery.includes('debt') || lowerQuery.includes('bills')) {
      situationSpecificAffirmations.financial = [
        "I am resourceful and capable of finding solutions to my financial challenges one step at a time",
        "My worth is not determined by my financial situation - I am valuable regardless of my circumstances",
        "I trust that I can make wise decisions about money and create more financial stability in my life"
      ];
    }
    
    // Check for situation-specific affirmations first
    for (const [situation, affirmations] of Object.entries(situationSpecificAffirmations)) {
      return affirmations[Math.floor(Math.random() * affirmations.length)];
    }
    
    // Fall back to concern-based affirmations
    const affirmations = {
      'anxiety': [
        "I am safe in this moment, and I have the inner resources to handle whatever comes my way",
        "My anxiety is information, not a command. I can feel it and still choose my response",
        "I am learning to trust myself and my ability to navigate uncertainty with grace"
      ],
      'depression': [
        "I am worthy of love and care, especially from myself, even when I don't feel it",
        "This darkness is temporary, and I am stronger than I know. I will find my way back to light",
        "I honor my struggles while also believing in my capacity for healing and growth"
      ],
      'stress': [
        "I can handle this one moment at a time, and I don't have to carry tomorrow's burdens today",
        "I am doing the best I can with the resources I have, and that is enough",
        "I give myself permission to rest and recharge without guilt"
      ],
      'relationships': [
        "I am worthy of healthy, loving relationships, and I can learn to create them",
        "I choose to approach my relationships with curiosity and compassion rather than judgment",
        "I can express my needs clearly and kindly, and I trust that love can grow through honest communication"
      ],
      'self-esteem': [
        "I am inherently valuable, not because of what I achieve, but because of who I am",
        "I choose to speak to myself with the same kindness I would offer a dear friend",
        "I am on a journey of growth, and every step forward, no matter how small, is worth celebrating"
      ]
    };
    
    // Select based on primary concern
    const primaryConcern = concerns[0] || 'general';
    const relationshipConcerns = ['relationships', 'work', 'family'];
    
    if (relationshipConcerns.includes(primaryConcern)) {
      return affirmations.relationships[Math.floor(Math.random() * affirmations.relationships.length)];
    }
    
    if (affirmations[primaryConcern]) {
      return affirmations[primaryConcern][Math.floor(Math.random() * affirmations[primaryConcern].length)];
    }
    
    // Default affirmation based on emotional state
    const defaultAffirmations = {
      'high-intensity': "I am resilient and capable of weathering this storm. I will find my way through.",
      'moderate': "I am growing stronger with each challenge I face, and I trust in my ability to heal.",
      'mild': "I am worthy of peace and joy, and I choose to nurture these feelings within myself."
    };
    
    return defaultAffirmations[emotionalState] || "I am exactly where I need to be in my journey, and I trust the process of growth.";
  },

  // Add missing methods that were referenced but not implemented
  identifyMainConcerns(query) {
    const concerns = [];
    const lowerQuery = query.toLowerCase();
    
    if (lowerQuery.includes('anxious') || lowerQuery.includes('nervous') || lowerQuery.includes('worried') || lowerQuery.includes('panic') || lowerQuery.includes('fear')) {
      concerns.push('anxiety');
    }
    if (lowerQuery.includes('sad') || lowerQuery.includes('depressed') || lowerQuery.includes('down') || lowerQuery.includes('hopeless') || lowerQuery.includes('empty')) {
      concerns.push('depression');
    }
    if (lowerQuery.includes('stress') || lowerQuery.includes('overwhelmed') || lowerQuery.includes('pressure') || lowerQuery.includes('burden')) {
      concerns.push('stress');
    }
    if (lowerQuery.includes('sleep') || lowerQuery.includes('tired') || lowerQuery.includes('insomnia') || lowerQuery.includes('exhausted') || lowerQuery.includes('restless')) {
      concerns.push('sleep');
    }
    if (lowerQuery.includes('work') || lowerQuery.includes('job') || lowerQuery.includes('boss') || lowerQuery.includes('career') || lowerQuery.includes('workplace')) {
      concerns.push('work');
    }
    if (lowerQuery.includes('relationship') || lowerQuery.includes('partner') || lowerQuery.includes('family') || lowerQuery.includes('friends') || lowerQuery.includes('social')) {
      concerns.push('relationships');
    }
    if (lowerQuery.includes('confidence') || lowerQuery.includes('self-worth') || lowerQuery.includes('worthless') || lowerQuery.includes('shame') || lowerQuery.includes('inadequate')) {
      concerns.push('self-esteem');
    }
    if (lowerQuery.includes('motivation') || lowerQuery.includes('energy') || lowerQuery.includes('exercise') || lowerQuery.includes('lazy') || lowerQuery.includes('procrastination')) {
      concerns.push('motivation');
    }
    
    return concerns;
  },

  detectEmotionalState(query) {
    const lowerQuery = query.toLowerCase();
    
    if (lowerQuery.includes('crisis') || lowerQuery.includes('suicide') || lowerQuery.includes('harm') || lowerQuery.includes('emergency')) {
      return 'crisis';
    }
    if (lowerQuery.includes('very') || lowerQuery.includes('extremely') || lowerQuery.includes('really') || lowerQuery.includes('severe')) {
      return 'high-intensity';
    }
    if (lowerQuery.includes('little') || lowerQuery.includes('slightly') || lowerQuery.includes('sometimes') || lowerQuery.includes('mild')) {
      return 'mild';
    }
    return 'moderate';
  },

  generateImmediateRelief(concerns, emotionalState) {
    if (emotionalState === 'crisis') {
      return [
        "🚨 If you're having thoughts of self-harm, please contact 988 (Suicide & Crisis Lifeline) immediately",
        "Text HOME to 741741 for Crisis Text Line support",
        "Go to your nearest emergency room or call 911 if you're in immediate danger",
        "You matter and there are people who want to help you through this"
      ];
    }

    const reliefStrategies = {
      anxiety: [
        "Ground yourself using the 5-4-3-2-1 technique: 5 things you see, 4 you touch, 3 you hear, 2 you smell, 1 you taste",
        "Place your hand on your chest and feel your heartbeat - remind yourself you're safe right now",
        "Try progressive muscle relaxation: tense and release each muscle group for 5 seconds",
        "Use cold water on your wrists or splash your face to activate your body's calm response"
      ],
      depression: [
        "Step outside for 2 minutes and feel sunlight on your skin",
        "Put on your favorite song and allow yourself to feel whatever comes up",
        "Do one tiny self-care act: brush your teeth, wash your face, or drink a glass of water",
        "Reach out to someone who cares about you, even if it's just sending a emoji"
      ],
      stress: [
        "Take 3 deep breaths and say 'I can handle this one step at a time'",
        "Write down everything overwhelming you, then circle what you can control today",
        "Do 10 jumping jacks or stretch to release physical tension",
        "Set a timer for 5 minutes and do something that brings you peace"
      ],
      sleep: [
        "Create a cozy environment: dim lights, comfortable temperature, soft sounds",
        "Try the 4-7-8 breathing technique to activate your relaxation response",
        "Write down 3 things you're grateful for today to shift your mindset",
        "Do gentle stretches or yoga poses to release physical tension"
      ]
    };

    const primaryConcern = concerns[0] || 'general';
    return reliefStrategies[primaryConcern] || [
      "Take 5 slow, deep breaths and focus on the present moment",
      "Remind yourself: 'This feeling is temporary and will pass'",
      "Do something kind for yourself right now, however small",
      "You're taking positive steps by seeking support - that takes courage"
    ];
  },

  generateBreathingExercises(concerns) {
    if (concerns.includes('anxiety') || concerns.includes('stress')) {
      return [
        "🫁 Box Breathing: Inhale for 4, hold for 4, exhale for 4, hold for 4. Repeat 5 times.",
        "🌊 Ocean Breath: Breathe in through nose, exhale with a soft 'ahh' sound. Feel the waves of calm.",
        "⭐ 4-7-8 Technique: Inhale for 4, hold for 7, exhale slowly for 8. Great for anxiety relief.",
        "🔄 Belly Breathing: Hand on chest, hand on belly. Breathe so only the bottom hand moves."
      ];
    } else if (concerns.includes('sleep')) {
      return [
        "🌙 Sleepy Breath: Inhale for 4, hold for 2, exhale for 6. Repeat until drowsy.",
        "☁️ Cloud Breathing: Imagine breathing in peace, breathing out tension with each cycle.",
        "🕯️ Candle Breath: Imagine blowing out a candle very slowly with each exhale.",
        "🌸 Lavender Breath: Breathe in calm (imagine lavender scent), breathe out stress."
      ];
    } else {
      return [
        "🧘 Mindful Breathing: Simply notice your breath without changing it. Count: 1 in, 2 out, up to 10.",
        "💚 Heart Breathing: Breathe into your heart space, imagining warmth and compassion.",
        "🌱 Energy Breath: Inhale vitality and life force, exhale what no longer serves you.",
        "🎯 Focus Breath: Count each exhale from 10 down to 1, restart if your mind wanders."
      ];
    }
  },

  generateCognitiveStrategies(concerns, emotionalState) {
    if (concerns.includes('anxiety')) {
      return [
        "🧠 Ask yourself: 'Is this thought helpful or harmful right now?' Choose helpful thoughts.",
        "📊 Rate your anxiety 1-10. Remind yourself that even 8s and 9s eventually become 3s and 4s.",
        "🔍 Challenge anxious thoughts: 'What evidence do I have that this will actually happen?'",
        "🎭 Imagine giving advice to a friend with your exact situation - what would you say?"
      ];
    } else if (concerns.includes('depression')) {
      return [
        "💭 Notice self-critical thoughts and replace them with neutral observations about yourself.",
        "📝 Write down one small thing you accomplished today, however tiny it seems.",
        "🌟 Remember: Depression lies. Your thoughts right now may not reflect reality.",
        "🔄 Practice the 'Yes, and...' technique: 'Yes, I feel sad, AND I am taking steps to feel better.'"
      ];
    } else if (concerns.includes('stress')) {
      return [
        "📋 Separate your worries into: 'Things I can control' and 'Things I cannot control.'",
        "⏰ Use the '10-10-10 rule': Will this matter in 10 minutes, 10 months, 10 years?",
        "🎯 Focus on progress, not perfection. What's one small step you can take right now?",
        "🔄 Reframe: Instead of 'I have to...', try 'I choose to...' or 'I get to...'"
      ];
    } else {
      return [
        "🌈 Practice perspective shifting: 'What else could this situation mean?'",
        "💪 Remind yourself of past challenges you've overcome successfully.",
        "🎨 Use the 'Best Friend Test': Would you talk to your best friend the way you talk to yourself?",
        "🔮 Imagine your wisest self giving you advice right now - what would they say?"
      ];
    }
  },

  generatePhysicalActions(concerns) {
    const physicalStrategies = {
      anxiety: [
        "Try the 'Butterfly Hug': Cross your arms and gently tap your shoulders alternately",
        "Do wall push-ups: 10 gentle push-ups against a wall to release nervous energy",
        "Practice 'Grounding': Press your feet firmly into the floor and notice the sensation",
        "Take a cold shower or splash cold water on your face to reset your nervous system"
      ],
      depression: [
        "Step outside for 5 minutes, even if it's just to your doorstep",
        "Do gentle stretches or yoga poses to reconnect with your body",
        "Dance to one song - movement can shift energy and mood",
        "Take a warm bath or shower to practice self-care"
      ],
      stress: [
        "Do 10 jumping jacks or run in place to release physical tension",
        "Practice progressive muscle relaxation: tense and release each muscle group",
        "Take a brisk 5-minute walk to clear your head",
        "Do some gentle neck and shoulder rolls to release carried tension"
      ],
      sleep: [
        "Do gentle yoga stretches or simple poses like child's pose",
        "Try a warm bath with Epsom salts to relax your muscles",
        "Practice gentle self-massage on your temples and shoulders",
        "Do some light stretching to release the day's tension"
      ]
    };

    const primaryConcern = concerns[0] || 'general';
    return physicalStrategies[primaryConcern] || [
      "Take 5 deep breaths while gently stretching your arms above your head",
      "Do some gentle neck rolls to release tension",
      "Step outside for fresh air and a change of scenery",
      "Practice good posture - sit or stand tall and breathe deeply"
    ];
  },

  generateLongTermStrategies(concerns) {
    if (concerns.includes('anxiety')) {
      return [
        "🧘 Use our Intelligent Meditation feature for personalized guided sessions tailored to your needs.",
        "📖 Start a worry journal: write anxious thoughts, then rational responses.",
        "🏥 Consider therapy, especially CBT (Cognitive Behavioral Therapy) for anxiety.",
        "🎯 Create an 'anxiety toolkit' with your favorite calming techniques ready to use."
      ];
    } else if (concerns.includes('depression')) {
      return [
        "🌅 Establish a morning routine that includes light exposure and one pleasant activity.",
        "👥 Schedule regular social connections, even if it's just a 5-minute call.",
        "🎨 Engage in creative expression: art, music, writing, or any form that speaks to you.",
        "💊 Consider speaking with a healthcare provider about therapy or medication options."
      ];
    } else if (concerns.includes('work')) {
      return [
        "⚖️ Set clear work-life boundaries: specific start/stop times and a transition ritual.",
        "📅 Use time-blocking to prioritize important tasks and prevent overwhelm.",
        "💬 Practice assertive communication with colleagues and supervisors about your needs.",
        "🎯 Regular career check-ins: Am I growing? Am I fulfilled? What needs to change?"
      ];
    } else if (concerns.includes('relationships')) {
      return [
        "💬 Practice 'I' statements in communication: 'I feel...' instead of 'You always...'",
        "📋 Set healthy boundaries and communicate them clearly and kindly.",
        "👂 Develop active listening skills: focus fully on understanding, not responding.",
        "💕 Schedule quality time for important relationships without distractions."
      ];
    } else {
      return [
        "📝 Develop a consistent self-care routine that you can maintain long-term.",
        "🎯 Set small, achievable goals that build momentum toward larger changes.",
        "🧠 Build emotional awareness through mindfulness or journaling practices.",
        "🌐 Create a support network of people you can turn to during difficult times."
      ];
    }
  },

  generateAffirmation(emotionalState) {
    const affirmations = [
      "You are worthy of love and care, especially from yourself",
      "You have overcome challenges before, and you will overcome this too",
      "Your feelings are valid, and it's okay to not be okay sometimes",
      "You are stronger than you know and braver than you feel",
      "This difficult moment will pass, and you will find peace again",
      "You deserve happiness, rest, and all good things in life",
      "You are enough, exactly as you are right now",
      "Your mental health matters, and taking care of yourself is important",
      "You have the power to choose how you respond to this situation",
      "You are not alone - there are people who care about you"
    ];
    
    return affirmations[Math.floor(Math.random() * affirmations.length)];
  },

  generateResources(concerns) {
    const resources = [];
    
    if (concerns.includes('anxiety')) {
      resources.push('Use our Intelligent Meditation feature for personalized guided sessions');
      resources.push('Consider the DARE technique for anxiety management');
    }
    
    if (concerns.includes('depression')) {
      resources.push('Visit 7cups.com for free emotional support');
      resources.push('Consider therapy through BetterHelp or local mental health services');
    }
    
    if (concerns.includes('sleep')) {
      resources.push('Use sleep tracking features in our wellness tools to monitor your sleep patterns');
      resources.push('Look into sleep hygiene resources from the Sleep Foundation');
    }
    
    // Add general resources
    resources.push('Crisis Text Line: Text HOME to 741741');
    resources.push('National Suicide Prevention Lifeline: 988');
    
    return resources;
  }
};
